import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, CartesianGrid, XAxis, YAxis, Bar } from 'recharts';
import { BarChart2, ChevronDown, Filter } from 'lucide-react';

const CategoryBreakdown = ({ data }) => {
  const [chartType, setChartType] = useState('bar');
  const [sentimentFilter, setSentimentFilter] = useState('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  
  // Process data based on sentiment filter
  const getFilteredData = () => {
    if (sentimentFilter === 'all') {
      return data;
    }
    
    // For other sentiment filters, we'll use the sentiment data if available
    return data.map(item => ({
      ...item,
      value: item[sentimentFilter] || item.value, // Fallback to total value if sentiment not available
    })).sort((a, b) => b.value - a.value);
  };
  
  const filteredData = getFilteredData();
  const total = filteredData.reduce((acc, item) => acc + item.value, 0);
  
  // Custom colors for categories
  const COLORS = [
    '#4f46e5', // indigo
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#f43f5e', // rose
    '#f97316', // orange
    '#eab308', // yellow
    '#22c55e', // green
    '#06b6d4', // cyan
    '#0ea5e9', // light blue
    '#6366f1'  // indigo (lighter)
  ];

  // Sentiment colors - updated for consistency
  const SENTIMENT_COLORS = {
    positive: '#10b981', // Green
    neutral: '#9ca3af',  // Gray
    negative: '#ef4444', // Red
  };
  
  // Format data with percentages for the chart
  const chartData = filteredData.map((item, index) => ({
    ...item,
    percentage: total > 0 ? Math.round((item.value / total) * 100) : 0,
    color: COLORS[index % COLORS.length]
  }));
  
  // Custom tooltip component
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="bg-white p-3 shadow-md rounded-md border border-gray-200">
          <p className="font-medium mb-1">{item.name}</p>
          <p className="text-sm">{item.value} {sentimentFilter === 'all' ? 'mentions' : `${sentimentFilter} mentions`}</p>
          
          {sentimentFilter === 'all' && (
            <div className="text-xs mt-2 pt-1 border-t border-gray-100">
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-emerald-500 mr-1.5"></div>
                Positive: {item.positive || 0}
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-gray-400 mr-1.5"></div>
                Neutral: {item.neutral || 0}
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-red-500 mr-1.5"></div>
                Negative: {item.negative || 0}
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };
  
  // Custom legend component for right side
  const renderRightLegend = () => {
    return (
      <div className="flex flex-col justify-center h-full pl-4">
        <div className="space-y-2">
          {chartData.slice(0, 8).map((entry, index) => (
            <div key={`legend-${index}`} className="flex items-center justify-between">
              <div className="flex items-center min-w-0 flex-1">
                <div 
                  className="w-3 h-3 rounded-full mr-3 flex-shrink-0" 
                  style={{ backgroundColor: sentimentFilter !== 'all' ? SENTIMENT_COLORS[sentimentFilter] : entry.color }}
                ></div>
                <div className="text-sm font-medium truncate">
                  {entry.name}
                </div>
              </div>
              <div className="text-sm text-gray-500 ml-3 flex-shrink-0">
                {entry.percentage}%
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // Get sentiment filter label
  const getSentimentFilterLabel = () => {
    switch (sentimentFilter) {
      case 'positive': return 'Positive Mentions';
      case 'negative': return 'Negative Mentions';
      case 'neutral': return 'Neutral Mentions';
      default: return 'All Mentions';
    }
  };

  // Get bar fill color based on sentiment filter
  const getBarFill = (entry) => {
    if (sentimentFilter !== 'all') {
      return SENTIMENT_COLORS[sentimentFilter];
    }
    return entry.color;
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 h-full"
    >
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Category Breakdown</h3>
          <p className="text-sm text-gray-500">Distribution of feedback by category</p>
        </div>
        <div className="flex space-x-2">
          <div className="relative">
            <button 
              className="flex items-center space-x-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
              onClick={() => setShowFilterMenu(!showFilterMenu)}
            >
              <Filter className="w-3.5 h-3.5 text-gray-500" />
              <span>{getSentimentFilterLabel()}</span>
              <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
            </button>
            
            {showFilterMenu && (
              <div className="absolute right-0 z-10 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                <div className="py-1" role="menu" aria-orientation="vertical">
                  <button
                    className={`block px-4 py-2 text-sm w-full text-left ${sentimentFilter === 'all' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                    onClick={() => {
                      setSentimentFilter('all');
                      setShowFilterMenu(false);
                    }}
                  >
                    All Mentions
                  </button>
                  <button
                    className={`block px-4 py-2 text-sm w-full text-left ${sentimentFilter === 'positive' ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                    onClick={() => {
                      setSentimentFilter('positive');
                      setShowFilterMenu(false);
                    }}
                  >
                    Positive Mentions
                  </button>
                  <button
                    className={`block px-4 py-2 text-sm w-full text-left ${sentimentFilter === 'neutral' ? 'bg-gray-50 text-gray-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                    onClick={() => {
                      setSentimentFilter('neutral');
                      setShowFilterMenu(false);
                    }}
                  >
                    Neutral Mentions
                  </button>
                  <button
                    className={`block px-4 py-2 text-sm w-full text-left ${sentimentFilter === 'negative' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                    onClick={() => {
                      setSentimentFilter('negative');
                      setShowFilterMenu(false);
                    }}
                  >
                    Negative Mentions
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex">
            <button 
              className={`p-2 rounded-l-lg ${chartType === 'bar' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              onClick={() => setChartType('bar')}
              title="Bar Chart"
            >
              <BarChart2 className="w-4 h-4" />
            </button>
            <button 
              className={`p-2 rounded-r-lg ${chartType === 'pie' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              onClick={() => setChartType('pie')}
              title="Pie Chart"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      <div className="h-80 mt-4">
        {chartType === 'pie' ? (
          <div className="flex h-full">
            {/* Pie Chart on the left */}
            <div className="w-3/5 h-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    innerRadius={0}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    isAnimationActive={true}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={sentimentFilter !== 'all' ? SENTIMENT_COLORS[sentimentFilter] : entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            {/* Legend on the right */}
            <div className="w-2/5 h-full">
              {renderRightLegend()}
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              layout="vertical" 
              data={chartData.slice(0, 8)}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={true} stroke="#e5e7eb" />
              <XAxis type="number" axisLine={{ stroke: '#d1d5db' }} tickLine={false} />
              <YAxis 
                dataKey="name" 
                type="category" 
                axisLine={{ stroke: '#d1d5db' }}
                tickLine={false}
                width={100}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarFill(entry)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      
      <div className="mt-4 text-xs text-gray-500 text-center">
        Based on {total} {sentimentFilter !== 'all' ? `${sentimentFilter} ` : ''}category mentions in customer feedback
      </div>
    </motion.div>
  );
};

export default CategoryBreakdown;